import type { Locale, ThemePreference } from '@miroom/shared';
import { create } from 'zustand';

const KEY = 'miroom.prefs';

interface Prefs {
  locale: Locale;
  theme: ThemePreference;
}

function detectLocale(): Locale {
  const lang = typeof navigator !== 'undefined' ? navigator.language : 'ru';
  return /^(ru|uk|be|kk)/i.test(lang) ? 'ru' : 'en';
}

function load(): Prefs {
  const fallback: Prefs = { locale: detectLocale(), theme: 'system' };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fallback;
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      locale: p.locale === 'en' || p.locale === 'ru' ? p.locale : fallback.locale,
      theme: p.theme === 'light' || p.theme === 'dark' || p.theme === 'system' ? p.theme : 'system',
    };
  } catch {
    return fallback;
  }
}

function save(p: Prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // storage unavailable (private mode) — prefs live for this session only
  }
}

interface PrefsState extends Prefs {
  setLocale(locale: Locale): void;
  setTheme(theme: ThemePreference): void;
}

/** Per-device UI preferences (also synced from the profile after login). */
export const usePrefs = create<PrefsState>((set, get) => ({
  ...load(),
  setLocale(locale) {
    set({ locale });
    save({ locale, theme: get().theme });
  },
  setTheme(theme) {
    set({ theme });
    save({ locale: get().locale, theme });
  },
}));
